import { Box, Container, Divider, Typography, Chip, Paper } from "@mui/material";
import { motion, Variants } from "framer-motion";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import GavelIcon from "@mui/icons-material/Gavel";
import EmojiObjectsIcon from "@mui/icons-material/EmojiObjects";
import GroupsIcon from "@mui/icons-material/Groups";
import PageMeta from '../components/PageMeta';

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.4, ease: "easeOut" as const },
  }),
};

const sections = [
  {
    icon: <AutoAwesomeIcon fontSize="small" />,
    title: "What is The Quizzler?",
    body: `The Quizzler is a fan-made trivia experience built for fans of the Wizarding World. 
    Test your knowledge across a range of categories and game modes, compete with others 
    on the leaderboard, and discover where your true loyalty lies. From casual fan to obsessive superfan — find your level.`,
  },
  {
    icon: <EmojiObjectsIcon fontSize="small" />,
    title: "Why we built it",
    body: `We built The Quizzler to stump our friends. Specifically, the ones who've
     read every book ten times, seen every movie, and still think they know everything. 
     Turns out a lot of people felt the same way — so we kept building, adding easier content
      alongside the brutal stuff. There's a level for everyone here, but we won't pretend the
       hard questions aren't our favorites.`,
  },
  {
    icon: <GroupsIcon fontSize="small" />,
    title: "Community & feedback",
    body: `The Quizzler is always growing. If you spot an error in a question, want to 
    suggest a new category, or just want to say hello — we'd love to hear from you. 
    This site is built for the community, and community input shapes what comes next.`,
  },
];

export default function AboutPage() {
  return (
    <>
      <PageMeta
        title="About | The Quizzler"
        description="Learn about The Quizzler — a fan-made Harry Potter trivia site built to challenge even the most devoted fans of the Wizarding World."
        canonical="https://thequizzler.app/about"
      />
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Typography
            variant="h3"
            fontWeight={700}
            gutterBottom
            sx={{ letterSpacing: "-0.5px" }}
          >
            About The Quizzler
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 560 }}>
            A fan-made trivia site for Wizarding World enthusiasts.
          </Typography>
          <Divider sx={{ mb: 4 }} />
        </motion.div>

        {/* Content sections */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 4, mb: 5 }}>
          {sections.map((section, i) => (
            <motion.div
              key={section.title}
              custom={i}
              initial="hidden"
              animate="visible"
              variants={fadeUp}
            >
              <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                <Box
                  sx={{
                    mt: "2px",
                    color: "primary.main",
                    flexShrink: 0,
                    width: 32,
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 1,
                    bgcolor: "#ba68c8",
                    "& svg": { color: "white" },
                  }}
                >
                  {section.icon}
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {section.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                    {section.body}
                  </Typography>
                </Box>
              </Box>
            </motion.div>
          ))}
        </Box>

        {/* Fan Site / Legal Disclaimer */}
        <motion.div
          custom={sections.length}
          initial="hidden"
          animate="visible"
          variants={fadeUp}
        >
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: "action.hover",
              borderColor: "divider",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <GavelIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                Fan Site Disclaimer
              </Typography>
              <Chip label="Not affiliated" size="small" variant="outlined" sx={{ ml: "auto" }} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
              The Quizzler is an independent, unofficial fan site created for entertainment purposes.
              It is{" "}
              <strong>not affiliated with, endorsed by, or connected to</strong>{" "}
              J.K. Rowling, Warner Bros. Entertainment, Wizarding World Digital, or any of their
              subsidiaries or licensees.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8, mt: 1.5 }}>
              All Harry Potter characters, names, and related indicia are trademarks of and
              &copy; Warner Bros. Entertainment Inc. and J.K. Rowling. All rights reserved.
              The Quizzler references these works for fan, commentary, and educational purposes
              and does not claim any ownership of third-party intellectual property.
            </Typography>
          </Paper>
        </motion.div>
      </Container>
    </>
  );
}